import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HubIcon from "@mui/icons-material/Hub";
import SaveIcon from "@mui/icons-material/Save";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { ticketsApi } from "../../../services/api/tickets/tickets";
import {
  generateWebhookToken,
  getSuggestedN8nCallbackUrl,
  toTestWebhookUrl,
} from "../../../utils/n8nWorkflowDefaults";

const WorkflowInfo = () => {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    webhook_url: "",
    webhook_token: "",
    callback_url: "",
    is_active: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tokenGenerated, setTokenGenerated] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const resolvedToken = () => {
    if (form.webhook_token.trim()) return form.webhook_token.trim();
    return null;
  };

  const canTest =
    form.webhook_url.trim() &&
    form.callback_url.trim() &&
    (resolvedToken() || settings?.token_set);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await ticketsApi.getWorkflowSettings();
      const suggestedCallback = getSuggestedN8nCallbackUrl();
      const apiCallback = (response.data.callback_url || "").trim();
      const useSuggested =
        !apiCallback ||
        (apiCallback.startsWith("/") && !apiCallback.startsWith("//"));
      setSettings(response.data);
      setForm({
        webhook_url: response.data.webhook_url || "",
        webhook_token: "",
        callback_url: useSuggested ? suggestedCallback : apiCallback,
        is_active: response.data.is_active ?? true,
      });
      setTokenGenerated(false);
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to load settings",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleGenerateToken = () => {
    setGeneratingToken(true);
    setError(null);
    try {
      const token = generateWebhookToken();
      setForm((prev) => ({ ...prev, webhook_token: token }));
      setShowToken(true);
      setTokenGenerated(true);
      setSuccess(
        "New token generated. Click Save settings to store it — copy it now if you need it in n8n.",
      );
    } catch (err) {
      setError(err.message || "Failed to generate token");
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleCopyToken = async () => {
    if (!form.webhook_token) return;
    try {
      await navigator.clipboard.writeText(form.webhook_token);
      setSuccess("Token copied to clipboard.");
    } catch {
      setError("Could not copy to clipboard.");
    }
  };

  const handleUseSuggestedCallback = () => {
    setForm((prev) => ({
      ...prev,
      callback_url: getSuggestedN8nCallbackUrl(),
    }));
    setTestResult(null);
  };

  const handleTestConnection = async () => {
    const token = resolvedToken();
    if (!form.webhook_url.trim()) {
      setError("Webhook URL is required to test.");
      return;
    }
    if (!form.callback_url.trim()) {
      setError("Callback URL is required to test.");
      return;
    }
    if (!token && !settings?.token_set) {
      setError("Generate or enter a webhook token before testing.");
      return;
    }
    if (!token) {
      setError(
        "Enter the webhook token in the field to test (saved token cannot be read back). Generate a new token or paste the existing one.",
      );
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setSuccess(null);
      setTestResult(null);

      const response = await ticketsApi.testWorkflowSettings({
        webhook_url: form.webhook_url.trim(),
        webhook_token: token,
        callback_url: form.callback_url.trim(),
        wait_for_callback: true,
        callback_timeout_seconds: 30,
      });

      setTestResult(response.data);
      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Connection test failed",
      );
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const payload = {
        webhook_url: form.webhook_url.trim() || null,
        callback_url: form.callback_url.trim() || null,
        is_active: form.is_active,
      };
      if (form.webhook_token.trim()) {
        payload.webhook_token = form.webhook_token.trim();
      } else if (!settings?.token_set) {
        setError(
          "Generate or enter a webhook token before saving (required for triggers and callbacks).",
        );
        setSaving(false);
        return;
      }

      const response = await ticketsApi.updateWorkflowSettings(payload);
      setSettings(response.data);
      setForm((prev) => ({ ...prev, webhook_token: "" }));
      setShowToken(false);
      setTokenGenerated(false);
      setSuccess("n8n workflow settings saved.");
    } catch (err) {
      setError(
        err.response?.data?.detail || err.message || "Failed to save settings",
      );
    } finally {
      setSaving(false);
    }
  };

  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        n8n Workflow Integration
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure the n8n webhook used to generate AI draft replies. After human
        review in the Tickets tab, replies are sent directly to Plenty.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <HubIcon color="primary" />
            <Typography variant="subtitle1">Workflow configuration</Typography>
            {settings && (
              <Chip
                size="small"
                color={settings.configured ? "success" : "warning"}
                label={settings.configured ? "Configured" : "Not configured"}
              />
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gap: 2 }}>
              <TextField
                fullWidth
                label="Webhook URL"
                placeholder="https://n8n.example.com/webhook/..."
                value={form.webhook_url}
                onChange={(e) => {
                  setTestResult(null);
                  setForm((prev) => ({
                    ...prev,
                    webhook_url: e.target.value,
                  }));
                }}
                helperText={
                  toTestWebhookUrl(form.webhook_url)
                    ? `Test URL (used by Test connection): ${toTestWebhookUrl(
                        form.webhook_url,
                      )}`
                    : "n8n production webhook URL — dashboard POSTs here when you click Generate via n8n"
                }
              />

              <TextField
                fullWidth
                label="Webhook token (X-API-KEY)"
                type={showToken ? "text" : "password"}
                placeholder={
                  settings?.token_set && !tokenGenerated
                    ? "Leave blank to keep existing token"
                    : "Generate a token or paste your own"
                }
                value={form.webhook_token}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    webhook_token: e.target.value,
                  }))
                }
                helperText="Same secret for both directions: dashboard → n8n (outbound trigger) and n8n → dashboard (callback). Configure the same value in your n8n HTTP Request node header X-API-KEY."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {form.webhook_token && (
                        <Tooltip title="Copy token">
                          <IconButton
                            size="small"
                            onClick={handleCopyToken}
                            edge="end"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    generatingToken ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AutorenewIcon />
                    )
                  }
                  onClick={handleGenerateToken}
                  disabled={generatingToken}
                >
                  Generate token
                </Button>
                {form.webhook_token && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowToken((v) => !v)}
                  >
                    {showToken ? "Hide token" : "Show token"}
                  </Button>
                )}
              </Box>

              <TextField
                fullWidth
                label="Callback URL"
                placeholder={getSuggestedN8nCallbackUrl()}
                value={form.callback_url}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    callback_url: e.target.value,
                  }))
                }
                helperText={
                  isLocalhost
                    ? `Local dev default: tickets backend on port ${
                        import.meta.env.VITE_TICKETS_API_PORT || "8006"
                      }. n8n must reach this URL (use ngrok/tunnel if n8n runs elsewhere).`
                    : "Public URL n8n POSTs to after generating a draft (included in the generate webhook payload as callback_url)."
                }
              />
              {isLocalhost && (
                <Button
                  size="small"
                  variant="text"
                  onClick={handleUseSuggestedCallback}
                  sx={{ justifySelf: "start", mt: -1 }}
                >
                  Reset to localhost default
                </Button>
              )}

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_active}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          is_active: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Workflow enabled"
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 4 }}
                >
                  When <strong>on</strong>, the Tickets UI can trigger n8n via
                  Generate via n8n (requires webhook URL + token). When{" "}
                  <strong>off</strong>, generate is blocked in the dashboard;
                  you can still test the n8n callback manually if the token is
                  saved. Use this to pause AI generation without deleting
                  credentials.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={
                    testing ? <CircularProgress size={16} /> : <PlayArrowIcon />
                  }
                  onClick={handleTestConnection}
                  disabled={testing || !canTest}
                >
                  {testing ? "Testing…" : "Test connection"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    saving ? <CircularProgress size={18} /> : <SaveIcon />
                  }
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save settings"}
                </Button>
              </Box>

              {testResult && (
                <Alert
                  severity={testResult.success ? "success" : "warning"}
                  sx={{ mt: 1 }}
                >
                  <Typography variant="body2" gutterBottom>
                    {testResult.message}
                  </Typography>
                  <Box
                    sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}
                  >
                    <Chip
                      size="small"
                      label={`Webhook HTTP ${
                        testResult.webhook_status_code ?? "?"
                      }`}
                      color={testResult.webhook_ok ? "success" : "error"}
                    />
                    <Chip
                      size="small"
                      label={
                        testResult.callback_received
                          ? `Callback OK (${testResult.callback_wait_seconds}s)`
                          : "No callback"
                      }
                      color={
                        testResult.callback_received ? "success" : "default"
                      }
                    />
                  </Box>
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Test connection</strong> calls the n8n{" "}
          <code>webhook-test</code> URL (not production). Your n8n workflow
          should branch on <code>action: test</code> and POST back to the
          callback with <code>action: test_complete</code> and the same{" "}
          <code>test_id</code>.
        </Typography>
        <Typography variant="body2" component="div" sx={{ mb: 1 }}>
          <strong>Should n8n return HTTP 200 immediately?</strong> Yes. Use
          &quot;Respond to Webhook&quot; → respond right away (200), then
          continue the workflow (AI, etc.) and call the callback when finished.
          Production generate works the same way: quick 200, async callback
          later with <code>generate_complete</code>.
        </Typography>
        <Typography variant="body2" component="div">
          <strong>Token usage:</strong> Dashboard sends{" "}
          <code>X-API-KEY: &lt;token&gt;</code> when calling n8n. n8n must send
          the same header when POSTing to the callback URL.
        </Typography>
      </Alert>
    </Box>
  );
};

export default WorkflowInfo;
